/**
 * MC Status Grid -- Auto-refresh polling module with DOM diffing and animations.
 *
 * Fetches server data every 30 seconds and smoothly updates cards without
 * page reload. Handles tab visibility, card reordering, player chip
 * transitions, and relative timestamp updates.
 */

import { McConsole } from './mc/console';
import { initAnalytics } from './mc/analytics';
import { initLeaderboard } from './mc/leaderboard';
import { initProfile } from './mc/profile';
import { initResourceHistory, appendLatest as appendLatestResourceHistory } from './mc/resourceHistory';
import { initControls } from './mc/controls';
import { initChatlog } from './mc/chatlog';
import { initRoster, updateRosterRows, type RosterRow } from './mc/roster';
import { initFeed } from './mc/feed';
import { initHeatmap } from './mc/heatmap';
import { initAllStats } from './mc/allStats';
import { initWaystoneSort } from './mc/waystoneSort';
import { initQuestDetail } from './mc/questDetail';
import { localizeInstant, wireInstantToUtcIso, formatRelative } from './utils/instants';
import { buildFleetTooltip, reconcileFleetPlayerFields, reconcilePlayerFields } from './utils/detailPollFields';
import { resolveMcLabel } from './utils/mcKeys';
import { POLL_INTERVAL } from './utils/poll';
import { compareServers } from './utils/serverSort';
import { buildMcAvatarTile, buildMcNameWrap } from './utils/mcIdentity';
import { fillMcValueCell } from './utils/mcValueFormat';
import * as bootstrap from 'bootstrap';

// Quick-task 260729-hy2 (Phase 235 live-poll regression fix) -- one active-effect entry, mirroring
// App\Utils\McPlayerPayload::forDetailPoll()'s wire shape exactly. `iconUrl` is safe to assign
// directly to `img.src` because it can only ever come from EffectSpriteMap's closed server-side
// enumeration (T-235-03) -- a modded/attacker effect id can never reach a path segment; do not
// "helpfully" relax this by letting the client construct a sprite URL itself.
interface EffectData {
    iconUrl: string | null;
    label: string;
    title: string | null;
    amplifierRoman: string | null;
}

interface PlayerData {
    uuid: string;
    // Phase 239 D-02/D-04/D-14 -- the resolved identity quartet. `McPlayerPayload::forPoll()` (one
    // shared builder, both the fleet poll and the detail poll) computes `display_name`/`unresolved`/
    // `is_online_mode` via `PlayerIdentity::displayName()` -- the SAME single resolution point
    // detail.latte/default.latte/leaderboard.ts's playerCell() all consume -- and sends the result
    // over the wire so this file never re-derives identity itself. Do NOT write a JS-side mirror of
    // `PlayerIdentity::displayName()`/`McHumanize` -- CONTEXT's Deferred Ideas is explicit that
    // needing one is a signal D-02 was implemented too narrowly. `linkable` mirrors
    // `PlayerInfo->hasProfile`, failing closed to `false` (D-14) -- an absent link, never a dead
    // one. All four are REQUIRED (not optional): both polls share this payload now, so every player
    // entry from either signal carries them, and marking them optional would let a future omission
    // degrade silently instead of failing at the type level. The old `name` key is GONE, not
    // aliased -- any stale consumer now fails loudly (a compiler error) instead of rendering
    // `undefined`.
    display_name: string;
    unresolved: boolean;
    is_online_mode: boolean;
    linkable: boolean;
    // Phase 231 D-02 -- push-only (heartbeat), detail-poll-only fields (RESEARCH Open Question 2:
    // the fleet poll's player entries never carry these keys at all, hence optional `?`; on the
    // detail poll a pull-path/ping player still carries the keys but with null values, hence the
    // nullable union). Guard on presence before reading, mirroring the shipped template's
    // `{if $player->dimension !== null}` discipline.
    dimension?: string | null;
    // Phase 241 D-01/D-03/D-28/D-30 -- the server-resolved imported-mod-name label riding
    // alongside its raw twin, never replacing it. Optional-and-nullable like every other
    // detail-poll-only field above: the fleet poll never carries these keys, and a stale bundle
    // briefly paired with an old payload must degrade via resolveMcLabel()'s own fallback rather
    // than throw. Route BOTH labels through the shared resolveMcLabel() at the one renderer call
    // site below -- never a hand-rolled `label ?? humanizeMcKey(raw)` expression here (D-30).
    dimensionLabel?: string | null;
    biome?: string | null;
    biomeLabel?: string | null;
    afk?: boolean | null;
    // Quick-task 260729-hy2 (Phase 235 VITL-01/PING-01 live-poll regression fix) -- the six
    // vitals fields, joining the SAME detail-poll-only/optional-and-nullable convention above.
    // Optional-and-nullable is deliberate: the fleet poll's player entries never carry these keys
    // at all (optional `?`), and a stale cached bundle briefly paired with a pre-fix payload (or
    // any other unexpected absence) must degrade to today's behaviour rather than throw (nullable
    // union) -- never assume presence, guard every read exactly like dimension/biome/afk above.
    healthLabel?: string | null;
    isLowHealth?: boolean | null;
    food?: number | null;
    foodTitle?: string | null;
    ping?: number | null;
    pingTier?: 'good' | 'ok' | 'bad' | null;
    effects?: EffectData[] | null;
}

interface ServerData {
    key: string;
    name: string;
    isOnline: boolean;
    // Phase 230 D-05/PANEL-07 -- Pterodactyl suspended flag, carried on every poll tick so the
    // badge/card-class can flip live without a page reload.
    isSuspended: boolean;
    onlinePlayers: number | null;
    maxPlayers: number | null;
    players: PlayerData[];
    version: string | null;
    motdHtml: string | null;
    favicon: string | null;
    latency: number | null;
    lastSeenAt: string | null;
    // Phase 231 D-03/D-04 -- presence source discriminator ('live'|'ping'), carried on BOTH poll
    // payloads (fleet handleGetStatus + detail handleGetServerStatus) so the Live/Ping badge stays
    // honest across the 30s poll. null means the resolver didn't run (pre-231 payload) -- the
    // detail-page badge updater removes its badge entirely in that case.
    presenceSource: 'live' | 'ping' | null;
}

/** Quick-task follow-up to Phase 253 — mirrors `McAnalyticsModel::getTodaySnapshot()`'s row shape
 *  exactly (same fields `App\Presenters\templates\Mc\detail.latte`'s Today section reads). */
interface TodayRow {
    uuid: string;
    display_name: string;
    unresolved: boolean;
    is_online_mode: boolean;
    linkable: boolean;
    // Phase 273 D-09/AFK-03 (Plan 13) -- mirrors getTodaySnapshot()'s own row-key insertion order:
    // `active_playtime_reason` is seeded before the metric loop populates `active_playtime`, so it
    // appears first on the wire too. Both are nullable independently of `baseline_incomplete` below
    // (see mcValueFormat.ts's fillMcValueCell() precedence: baselineIncomplete wins when both are
    // true for the same row).
    active_playtime_reason: string | null;
    active_playtime: number | null;
    playtime: number | null;
    blocks_mined: number | null;
    ground: number | null;
    water: number | null;
    air_vehicle: number | null;
    mob_kills: number | null;
    deaths: number | null;
    baseline_incomplete: boolean;
}

/** Mirrors `decorateWindow()`'s wrapper shape around `getTodaySnapshot()`'s `rows`. `null` only
 *  when this server has no analytics id at all (matches `actionDetail()`'s own null-vs-[]
 *  distinction — never conflated with an empty `rows` array, which means "has analytics, nobody
 *  played today"). */
interface TodayPayload {
    has_data: boolean;
    rows: TodayRow[];
    window_boundary_note: string;
}

interface DetailServerData extends ServerData {
    address: string;
    bluemapUrl: string | null;
    today: TodayPayload | null;
    roster: RosterRow[];
}

// Phase 257 (257-04, D-11) -- the "last fetch" indicator's own re-age cadence, deliberately
// SEPARATE from POLL_INTERVAL (utils/poll.ts, the 30s fetch cadence) and from shared.ts's
// app-wide 60s relative-time ticker. The operator explicitly chose 5 seconds over the
// initially-recommended 10: a 60s re-age is too coarse against a 30s poll cadence, and this
// element needs its own tighter tick, not a shared one.
const LAST_FETCH_TICK_MS = 5_000;

// Phase 259 D-13 -- module-scoped idempotency flag for the 401 -> /login interceptor installed at
// the top of static init() below. mc.init() runs exactly once per page load today, but this repo
// has a documented scar (shared components double-binding handlers on repeat init) -- the same
// discipline as picker.ts's `if (el._flatpickr) return` guard, applied here at module scope since
// there is no per-element target to stash a marker on.
let mcFetchInterceptorInstalled = false;

/**
 * WR-02 fix (Phase 259 code review). Decides whether an intercepted fetch() targeted an MC route.
 *
 * Two defects it replaces, both in the old one-liner
 * `const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;`:
 *
 * 1. `Parameters<typeof fetch>[0]` is `RequestInfo | URL`, and a `URL` argument is neither a
 *    string nor a Request -- `.url` came back `undefined` and `.includes()` threw a TypeError
 *    INSIDE the wrapper, after a successful response, turning a perfectly good 200 into a
 *    rejected promise. No in-repo caller passes a URL today, but the monkey-patch is global and
 *    any future or third-party caller inherits it.
 * 2. `url.includes('/mc')` was an unanchored substring test over the ENTIRE url, query string
 *    included, so `/mcp`, `/x/mcache` or any `?q=/mc...` value matched. mc.init() runs on every
 *    page (src/index.ts), so that over-match was app-wide.
 *
 * Anchoring on the resolved pathname matches RouterFactory's actual MC surface exactly: `/mc`
 * plus every `/mc/...` route. Signal URLs keep working because `?do=` lives in the query, not
 * the path. Anything unparseable is treated as not-MC -- a fetch that 401s is never worth
 * throwing over.
 */
function mcIsMcRequest(input: Parameters<typeof fetch>[0]): boolean {
    try {
        const raw = typeof input === 'string' ? input
            : input instanceof URL ? input.href
            : input.url;
        const path = new URL(raw, location.origin).pathname;
        return path === '/mc' || path.startsWith('/mc/');
    } catch {
        return false;
    }
}

export default class {
    private static grid: HTMLElement | null = null;
    private static statusUrl: string = '';
    private static pollTimer: ReturnType<typeof setTimeout> | null = null;
    private static lastFetchAt: number | null = null;
    private static fetchTicker: ReturnType<typeof setInterval> | null = null;

    static init(): void {
        // Phase 259 D-13 -- centralized 401 -> /login fetch interceptor. Installed as the very
        // FIRST statement of init(), before initAnalytics() and every other submodule init() call
        // below, so every later fetch() in every mc/*.ts submodule (~35 call sites, two
        // incompatible response-checking idioms: resp.ok data-pollers and parsed-body data.ok
        // signal-responses) is already covered by the time it runs. Deliberately a URL substring
        // filter, not a page/DOM-marker gate: mc.init() itself runs unconditionally on EVERY page
        // load (src/index.ts), so a page-scoped guard here would either miss some MC pages or leak
        // the redirect onto unrelated pages' own fetches (e.g. Transactions' AJAX), which is
        // outside this phase's scope. The wrapper returns the native response unchanged in every
        // case -- including the 401 case -- so both the resp.ok and data.ok idioms keep working
        // exactly as they do today for every non-401 case and for 401s outside /mc.
        if (!mcFetchInterceptorInstalled) {
            mcFetchInterceptorInstalled = true;
            const nativeFetch = window.fetch.bind(window);
            window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
                const resp = await nativeFetch(...args);
                if (resp.status === 401 && mcIsMcRequest(args[0])) {
                    // IN-08 fix. The obvious form -- location.href = '/login?backlink=' + the
                    // current path -- is WRONG in this codebase: `backlink` is not a URL, it is a
                    // short session key minted by Presenter::storeRequest() and redeemed by
                    // restoreRequest(). Handing it a path is a guaranteed miss, so it would look
                    // like a fix while still dumping the user on Home after login.
                    //
                    // Re-navigating to the CURRENT page instead reuses the server-side D-15
                    // machinery that already works: this is a document navigation (not a fetch, so
                    // the interceptor cannot re-enter), it arrives at McPresenter::load() as a
                    // real non-AJAX request, and that gate calls storeRequest() and redirects to
                    // /login?backlink=<real key> on its own. The user comes back to the exact MC
                    // page -- query string included -- instead of Home. If the 401 was spurious
                    // and the session is in fact live, the reload simply re-renders the page.
                    location.assign(location.href);
                }
                return resp;
            };
        }

        // Always attempt analytics init (guard inside checks for #mc-analytics-chart)
        initAnalytics();

        // Phase 228. Called unconditionally on every MC page for the same reason initAnalytics()
        // is: each module opens with its own DOM-presence guard and returns immediately when its
        // page's marker element is absent. Their markers are deliberately DISTINCT ids
        // (#mc-metric-select vs #mc-profile-metric-select), so neither can bind to the other's
        // controls — a shared id would let the leaderboard module drive the profile's selectors.
        initLeaderboard();
        initProfile();

        // Phase 232 (FEED-01..04). Same unconditional-call/self-guard idiom as the three modules
        // above — initFeed() opens with its own #mc-feed-stream presence guard and no-ops on every
        // page except /mc/feed. Wired here in mc.ts's own static init(), NOT in src/index.ts
        // (232-RESEARCH.md Pitfall 7 — the documented wrong wiring point for a per-page MC module).
        initFeed();

        // Phase 233 (HEAT-01/02). Same unconditional-call/self-guard idiom as initFeed() above --
        // initHeatmap() opens with its own #mc-heatmap-canvas presence guard and no-ops on every
        // page except /mc/heatmap. Wired here in mc.ts's own static init(), NOT in src/index.ts
        // (233-PATTERNS.md's corrected wiring point -- the same Ph232 Pitfall 7 trap: src/index.ts
        // never imports any ./js/mc/* submodule, only the unrelated EO section's default export).
        initHeatmap();

        // Phase 248 (D-03/D-07/D-08/D-09/D-10/D-12/D-13). Same unconditional-call/self-guard idiom
        // as initFeed()/initHeatmap() above -- initAllStats() opens with its own
        // .datatable[data-table="mc-all-stats"] presence guard and no-ops on every page except
        // /mc/all-stats. Wired here in mc.ts's own static init(), NOT in src/index.ts, which never
        // imports any ./js/mc/* submodule (the same documented wrong wiring point as above).
        initAllStats();

        // Phase 271 (271-07, D-09/D-12). Same unconditional-call/self-guard idiom as
        // initFeed()/initHeatmap()/initAllStats() above -- initWaystoneSort() opens with its own
        // .mc-waystone-table presence guard and no-ops on every page except /mc/<key>/waystones.
        // Wired here in mc.ts's own static init(), NOT inside initDetail() (that runs specifically
        // for /mc/<key>, a different route than the Waystones page) and NOT in src/index.ts, which
        // never imports any ./js/mc/* submodule (the same documented wrong wiring point as above).
        initWaystoneSort();

        // Phase 276 (QNAME-04, D-09/D-10). Same unconditional-call/self-guard idiom as
        // initFeed()/initHeatmap()/initAllStats()/initWaystoneSort() above -- initQuestDetail()
        // opens with its own .mc-quest-detail-trigger presence guard and no-ops on every page
        // with no trigger rendered. Both the server-rendered Quests board (quests.latte, plan
        // 276-06) and the JS-built Activity Feed (feed.ts, plan 276-09) emit this trigger class,
        // so this single call covers both surfaces -- the feed's own per-append rows call
        // attachQuestDetailTriggers() directly (idempotent, safe to call repeatedly) rather than
        // re-running this whole-document pass on every poll tick.
        initQuestDetail();

        // Phase 238 Plan 15 (D-10). Same unconditional-call/self-guard idiom as the calls above,
        // scoped to document.body rather than a single page's root element: the fleet page's
        // #mc-archived-accordion (its many .mc-restore-btn rows) is a SIBLING of .mc-grid, not a
        // descendant, and the fleet page never reaches initDetail()/its own initControls() call
        // below (the `if (this.grid) { ... return; }` branch exits first) -- without this call
        // those Restore buttons would render with zero JS wiring, the exact dead-button trap
        // Phase 230's own {link} handler-name regression already burned this project on once.
        // controls.ts's own inited guard makes the later initDetail() call (detail pages only) a
        // harmless no-op when this one already ran.
        initControls(document.body);

        this.grid = document.querySelector('.mc-grid');
        if (this.grid) {
            this.statusUrl = this.grid.dataset.statusUrl ?? '';
            if (!this.statusUrl) return;

            // Start polling. Relative-time rendering is now handled by shared.ts's 60s ticker
            // plus each <time> node's own localizeInstant() call on creation (D-02/D-03).
            this.schedulePoll();

            // Phase 257 (257-04, D-09/D-10, RESEARCH.md Open Question 1 RESOLVED) -- seed the
            // "last fetch" indicator immediately at init() rather than leaving it blank until the
            // first poll tick lands (up to 30s later). The server-rendered page IS itself a fresh
            // read of the same source, so seeding from page-load time is honest, not a fabricated
            // freshness claim.
            this.lastFetchAt = Date.now();
            this.renderLastFetch();
            this.startFetchTicker();

            // Phase 257 (257-04, D-06/D-08) -- manual refresh button. Calls this.poll() directly:
            // no public wrapper, no second fetch path. `private static` is a compile-time,
            // class-body-scoped check in TypeScript with no runtime effect, so this click handler
            // -- a closure defined inside this same static method -- can reach poll() as-is.
            const refreshBtn = document.querySelector<HTMLButtonElement>('.mc-refresh-btn');
            if (refreshBtn) {
                const refreshIcon = refreshBtn.querySelector<HTMLElement>('.mc-refresh-btn__icon');
                const refreshSpinner = refreshBtn.querySelector<HTMLElement>('.mc-refresh-btn__spinner');
                refreshBtn.addEventListener('click', () => {
                    refreshBtn.disabled = true;
                    refreshIcon?.classList.add('d-none');
                    refreshSpinner?.classList.remove('d-none');
                    // D-07: no cooldown, no in-flight guard, no AbortController -- the disabled
                    // state above is the whole mechanism. poll() never rejects (it swallows every
                    // failure internally), so .finally() here is defensive future-proofing that
                    // also satisfies D-08's "re-enable silently on failure too" requirement.
                    this.poll().finally(() => {
                        refreshBtn.disabled = false;
                        refreshIcon?.classList.remove('d-none');
                        refreshSpinner?.classList.add('d-none');
                    });
                });
            }

            // Visibility API: pause when hidden, immediate fetch on return
            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    this.clearPoll();
                    this.stopFetchTicker();
                } else {
                    this.poll();
                    // Phase 257 (257-04, D-11/D-15) -- a tab hidden for minutes would otherwise
                    // keep showing its stale pre-hide "Updated N seconds ago" until the resumed
                    // fetch lands; re-render immediately on resume, then resume the ticker.
                    this.renderLastFetch();
                    this.startFetchTicker();
                }
            });
            return;
        }

        const detail = document.querySelector<HTMLElement>('.mc-detail');
        if (detail) {
            initDetail(detail);
            return;
        }
    }

    private static schedulePoll(): void {
        this.pollTimer = setTimeout(() => this.poll(), POLL_INTERVAL);
    }

    private static clearPoll(): void {
        if (this.pollTimer !== null) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    // Phase 257 (257-04, D-09/D-11) -- renders `#mc-last-fetch` from `lastFetchAt`. Called by the
    // init() seed, the poll() success branch, the visibility-resume path, and the ticker below.
    private static renderLastFetch(): void {
        const el = document.getElementById('mc-last-fetch');
        if (!el || this.lastFetchAt === null) return;
        // textContent only -- T-231-07's discipline for this file, and there is nothing here to
        // escape anyway: both operands are Date.now() arithmetic, never wire data.
        // Clamped to >= 0 (257-REVIEW WR-03) -- a real system clock adjustment between the two
        // Date.now() reads (NTP resync, laptop sleep/resume) can otherwise go negative, which
        // formatRelative()/Intl.RelativeTimeFormat renders as "in N seconds" instead of "N seconds
        // ago". instants.ts's own localizeInstant() treats this exact clamp as mandatory (D-04).
        const diffMs = Math.max(0, Date.now() - this.lastFetchAt);
        el.textContent = `Updated ${formatRelative(diffMs)}`;
    }

    // Phase 257 (257-04, D-11/T-257-14) -- idempotent: a second call while a ticker already runs
    // is a no-op, so a tab hidden/shown many times over a session can never leak more than one
    // interval.
    private static startFetchTicker(): void {
        if (this.fetchTicker !== null) return;
        this.fetchTicker = setInterval(() => this.renderLastFetch(), LAST_FETCH_TICK_MS);
    }

    private static stopFetchTicker(): void {
        if (this.fetchTicker !== null) {
            clearInterval(this.fetchTicker);
            this.fetchTicker = null;
        }
    }

    private static async poll(): Promise<void> {
        this.clearPoll();

        try {
            const resp = await fetch(this.statusUrl, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (!resp.ok) {
                console.error(`[mc] Poll failed: ${resp.status}`);
            } else {
                const data: ServerData[] = await resp.json();
                updateGrid(data);
                // Phase 257 (257-04, D-10) -- LAST SUCCESSFUL fetch only. This stays inside the
                // success branch, never in `catch`, `finally`, or before the `try`: an indicator
                // that keeps advancing through an outage would tell the operator the fleet read is
                // fresh at exactly the moment it is not.
                this.lastFetchAt = Date.now();
                this.renderLastFetch();
            }
        } catch (err) {
            console.error('[mc] Poll error:', err);
        }

        this.schedulePoll();
    }
}

// ---------------------------------------------------------------------------
// Grid update (DOM diffing)
// ---------------------------------------------------------------------------

function updateGrid(servers: ServerData[]): void {
    // D-07 note: no archived branch belongs in this loop -- the fleet exclusion is server-side (238-08), so an archived server never enters `servers` in the first place; a client-side check here would be a second source of truth for the same rule.
    for (const server of servers) {
        const col = document.querySelector<HTMLElement>(
            `.mc-card-col[data-server-key="${server.key}"]`,
        );
        if (!col) continue;

        const card = col.querySelector<HTMLElement>('.mc-card');
        if (!card) continue;

        // Status class -- mc-card--suspended checked FIRST (Phase 230 D-05): a suspended server
        // never shows the plain online/offline color even if isOnline incidentally computes true.
        card.classList.toggle('mc-card--suspended', server.isSuspended);
        card.classList.toggle('mc-card--online', !server.isSuspended && server.isOnline);
        card.classList.toggle('mc-card--offline', !server.isSuspended && !server.isOnline);

        // Suspended badge row
        updateSuspendedBadge(card, server);

        // WR-01 (231-REVIEW): the Live/Ping presence badge on the fleet card, kept honest across
        // the 30s poll. handleGetStatus() ships presenceSource on every fleet payload; without
        // this updater the badge would freeze at server-render first-paint (a card could flip to
        // its offline color while still showing a stale "Live" badge until a full reload).
        updatePresenceSourceBadge(card, server);

        // Server name.
        // CR-01 (239-REVIEW): Phase 239 D-13/D-15 moved the card's clickable target INSIDE the
        // <h6> (`<h6><a class="stretched-link">…</a></h6>`, default.latte:80-82). Writing
        // `textContent` on the <h6> replaces ALL its children with one text node, deleting that
        // anchor -- and since .mc-card has no click handler of its own, the whole fleet grid
        // stopped being navigable ~30s after first paint. Target the anchor when the 239 markup
        // is present; fall back to the <h6> only for pre-239 cached markup.
        const nameEl = card.querySelector<HTMLElement>('h6 a.stretched-link')
            ?? card.querySelector<HTMLElement>('h6');
        if (nameEl) nameEl.textContent = server.name;

        // Latency badge
        updateLatency(card, server);

        // Version
        const versionEl = card.querySelector<HTMLElement>('.mc-card-version');
        if (versionEl) versionEl.textContent = server.version ?? 'Unknown';

        // MOTD -- DELETED, CR-03 (240-REVIEW).
        //
        // There is no `.mc-motd` element on this page (or on the detail page): commit de91668f
        // ("Remove MOTD display from grid cards and detail page (prevents double name)") deleted
        // `<div class="mc-motd">{$server->motdHtml|noescape}</div>` from BOTH default.latte and
        // detail.latte and left this updater behind. `card.querySelector('.mc-motd')` has been
        // returning null on every poll tick ever since, so the `motdEl.innerHTML = server.motdHtml`
        // that stood here was unreachable -- but it was a live landmine: re-adding a `.mc-motd`
        // element to the template would have silently re-armed an unescaped raw-HTML sink fed by
        // a REMOTE Minecraft server's own MOTD, on an anonymously-reachable page.
        //
        // If MOTD display is ever wanted back: `server.motdHtml` is still on the wire
        // (McPresenter::handleGetStatus/handleGetServerStatus) and is produced exclusively by
        // App\Mc\MotdParser, which after the CR-03 fix escapes text nodes AND allow-lists the
        // colour it puts in a style attribute. Even so, do NOT reintroduce `innerHTML` here --
        // T-231-07 is "textContent only, never innerHTML" and this file has now violated it once.
        // Parse into elements, or render the plain `motd.clean` text via textContent.

        // Favicon
        const iconImg = card.querySelector<HTMLImageElement>('.mc-card-icon img');
        if (iconImg && server.favicon) {
            iconImg.src = server.favicon;
        }

        // Player section vs offline info
        updatePlayerSection(card, server);
    }

    // Reorder cards
    reorderCards(servers);

    // Summary stats
    updateSummary(servers);
}

// ---------------------------------------------------------------------------
// Latency
// ---------------------------------------------------------------------------

function updateLatency(card: HTMLElement, server: ServerData): void {
    let badge = card.querySelector<HTMLElement>('.mc-latency');

    if (server.isOnline && server.latency !== null) {
        if (!badge) {
            // Create badge if it doesn't exist (server just came online)
            const headerRow = card.querySelector('.d-flex.align-items-center.justify-content-between');
            if (headerRow) {
                badge = document.createElement('span');
                badge.className = 'badge mc-latency';
                headerRow.appendChild(badge);
            }
        }
        if (badge) {
            badge.textContent = `${Math.round(server.latency)}ms`;
            badge.classList.remove('mc-latency--good', 'mc-latency--ok', 'mc-latency--bad');
            if (server.latency < 50) {
                badge.classList.add('mc-latency--good');
            } else if (server.latency <= 150) {
                badge.classList.add('mc-latency--ok');
            } else {
                badge.classList.add('mc-latency--bad');
            }
            badge.style.display = '';
        }
    } else if (badge) {
        badge.style.display = 'none';
    }
}

// ---------------------------------------------------------------------------
// Suspended badge (Phase 230 D-05) -- fleet card
// ---------------------------------------------------------------------------

function updateSuspendedBadge(card: HTMLElement, server: ServerData): void {
    let row = card.querySelector<HTMLElement>('.mc-suspended-row');

    if (server.isSuspended) {
        if (!row) {
            // Create the row if it doesn't exist (server just became suspended this tick) --
            // inserted before .mc-card-version, mirroring default.latte's server-rendered
            // placement. Fixed literal markup, no server-controlled data interpolated.
            const infoContainer = card.querySelector<HTMLElement>('.mc-card-info');
            const versionEl = card.querySelector<HTMLElement>('.mc-card-version');
            if (infoContainer) {
                row = document.createElement('div');
                row.className = 'mc-suspended-row mb-1';
                row.innerHTML = '<span class="badge bg-dark text-white" title="Controls disabled while suspended">Suspended</span>';
                if (versionEl) {
                    infoContainer.insertBefore(row, versionEl);
                } else {
                    infoContainer.prepend(row);
                }
            }
        }
    } else if (row) {
        row.remove();
    }
}

// WR-01 (231-REVIEW): find-or-create Live/Ping presence badge on a FLEET card, mirroring the
// detail page's updateDetailPresenceSource() exactly (same allow-list class discipline, same
// textContent-only writes) so the fleet surface stays honest across the 30s poll instead of
// freezing at server-rendered first-paint. Inserted in the SAME DOM position default.latte
// renders it: directly after .mc-card-version, before the ingest-health row below.
function updatePresenceSourceBadge(card: HTMLElement, server: ServerData): void {
    const info = card.querySelector<HTMLElement>('.mc-card-info');
    if (!info) return;

    let row = info.querySelector<HTMLElement>('.mc-presence-source-row');

    if (server.presenceSource !== 'live' && server.presenceSource !== 'ping') {
        // null -- pre-231 payload / resolver didn't run. Remove any existing badge rather than
        // leave a stale source showing once the discriminator itself goes missing (D-05 honesty).
        if (row) row.remove();
        return;
    }

    if (!row) {
        row = document.createElement('div');
        row.className = 'mc-presence-source-row mb-1';
        const badgeEl = document.createElement('span');
        badgeEl.className = 'badge mc-presence-source';
        row.appendChild(badgeEl);

        const versionEl = info.querySelector('.mc-card-version');
        if (versionEl) {
            versionEl.after(row);
        } else {
            info.appendChild(row);
        }
    }

    const badge = row.querySelector<HTMLElement>('.mc-presence-source');
    if (!badge) return;

    // T-231-10: the class is chosen from a fixed live/ping allow-list literal, never interpolated
    // from server-controlled data.
    badge.classList.remove('mc-presence-source--live', 'mc-presence-source--ping');
    badge.classList.add(
        server.presenceSource === 'live' ? 'mc-presence-source--live' : 'mc-presence-source--ping',
    );
    // T-231-07: textContent only, never innerHTML.
    badge.textContent = server.presenceSource === 'live' ? 'Live' : 'Ping';
    badge.title = server.presenceSource === 'live'
        ? 'Presence from live heartbeat telemetry'
        : 'Presence from server ping';
}

// ---------------------------------------------------------------------------
// Player section
// ---------------------------------------------------------------------------

function updatePlayerSection(card: HTMLElement, server: ServerData): void {
    let offlineDiv = card.querySelector<HTMLElement>('.mc-offline-info');
    let rightCol = card.querySelector<HTMLElement>('.mc-card-right');
    const infoContainer = card.querySelector<HTMLElement>('.mc-card-info');

    if (server.isOnline) {
        // Remove offline info. Dispose any bound Tooltip first (257-REVIEW WR-01) -- Bootstrap's
        // Data registry (node_modules/bootstrap/js/dist/dom/data.js) keys instances on a plain Map,
        // not a WeakMap, so discarding this node without dispose() leaks it for the tab's lifetime.
        // A server that flaps offline/online repeatedly while a tab stays open would otherwise
        // accumulate one leaked node + Tooltip instance per cycle.
        if (offlineDiv) {
            const boundTime = offlineDiv.querySelector<HTMLTimeElement>('time[data-bs-toggle="tooltip"]');
            if (boundTime) bootstrap.Tooltip.getInstance(boundTime)?.dispose();
            offlineDiv.remove();
        }

        // Player heads go in .mc-card-info (third line, aligned with "Last seen:")
        if (infoContainer) {
            diffPlayerHeads(infoContainer, server.players);
        }

        // Player count stays in .mc-card-right
        if (!rightCol) {
            rightCol = document.createElement('div');
            rightCol.className = 'mc-card-right flex-shrink-0';
            const cardBody = card.querySelector('.mc-card-body');
            if (cardBody) cardBody.appendChild(rightCol);
        }

        // Remove any stale heads from right col (migration from old layout)
        const oldHeads = rightCol.querySelector('.mc-player-heads');
        if (oldHeads) oldHeads.remove();

        let countEl = rightCol.querySelector<HTMLElement>('.mc-player-count');
        if (!countEl) {
            countEl = document.createElement('div');
            countEl.className = 'mc-player-count small text-muted';
            rightCol.prepend(countEl);
        }
        countEl.textContent = `${server.onlinePlayers}/${server.maxPlayers}`;
    } else {
        // Remove right column (count)
        if (rightCol) rightCol.remove();

        // Remove any player heads from info container
        if (infoContainer) {
            const heads = infoContainer.querySelector('.mc-player-heads');
            if (heads) heads.remove();
        }

        if (!offlineDiv) {
            offlineDiv = document.createElement('div');
            offlineDiv.className = 'mc-offline-info small text-muted';
            if (infoContainer) infoContainer.appendChild(offlineDiv);
        }

        if (server.lastSeenAt) {
            let timeEl = offlineDiv.querySelector<HTMLTimeElement>('time[data-utc]');

            // CR-01 (Phase 240 code review) -- this line USED to read `timeEl.dataset.utc =
            // server.lastSeenAt` under the comment "already an ISO-8601 string with an offset
            // (->format('c')) -- no conversion needed, none added." That comment was wrong and the
            // bug it caused was live and user-visible. `->format('c')` on a Nette-hydrated DATETIME
            // emits the naive-UTC DB digits under a BOGUS runtime-Toronto offset, so `new Date()`
            // put this instant up to 4h in the FUTURE (measured: 14400s), which trips
            // localizeInstant()'s `diffMs <= 60_000` clamp and made an offline server read "Last
            // seen: just now". wireInstantToUtcIso() is the exact reinterpretation Tz::toUtcIso()
            // performs server-side -- one definition, both mirrors of this field, no drift. Do not
            // "simplify" this back to a raw assignment.
            //
            // Phase 257 (257-03, D-12) -- this branch USED to also rebuild `.mc-offline-info` from
            // scratch on EVERY tick (clearing the whole div's text and appending a brand-new
            // <time> element), which destroyed the server-rendered <time> ~30s after first paint (the CR-01 bug
            // above) AND orphaned any Bootstrap Tooltip instance bound to that node the instant the
            // node was replaced -- a second, independent failure mode with the same root cause. Node
            // identity now survives the tick: an existing time[data-utc] node is found and mutated
            // in place instead of destroyed, so both the CR-01 timestamp fix and any bound Tooltip
            // instance survive every poll.
            const utc = wireInstantToUtcIso(server.lastSeenAt);

            if (!timeEl) {
                offlineDiv.textContent = '';
                offlineDiv.append('Last seen: ');
                timeEl = document.createElement('time');
                timeEl.dataset.relative = 'always'; // D-01: "Last seen" is relative-forever
                timeEl.setAttribute('data-bs-toggle', 'tooltip'); // D-12: styled tooltip opt-in
                offlineDiv.appendChild(timeEl);
            }

            if (utc !== null) {
                timeEl.dataset.utc = utc;
            }
            localizeInstant(timeEl); // D-03: populate on creation, don't wait for the 60s ticker

            // Get-or-create only -- _getTitle() re-reads data-bs-original-title fresh on every
            // show, so no dispose()/reinit cycle is ever needed here; constructing once and
            // mutating the node's attributes on every subsequent tick is sufficient (D-12).
            if (!bootstrap.Tooltip.getInstance(timeEl)) {
                new bootstrap.Tooltip(timeEl, { html: false, sanitize: true });
            }
        } else {
            // Same dispose-before-discard as the isOnline branch above (257-REVIEW WR-01) -- a
            // server can lose its lastSeenAt (rare, but the branch exists) after already having a
            // bound Tooltip on the <time> this textContent write is about to clear.
            const boundTime = offlineDiv.querySelector<HTMLTimeElement>('time[data-bs-toggle="tooltip"]');
            if (boundTime) bootstrap.Tooltip.getInstance(boundTime)?.dispose();
            offlineDiv.textContent = 'Never seen online';
        }
    }
}

// ---------------------------------------------------------------------------
// Player chip diffing
// ---------------------------------------------------------------------------

function diffPlayerHeads(container: HTMLElement, players: PlayerData[]): void {
    let headsDiv = container.querySelector<HTMLElement>('.mc-player-heads');

    if (players.length === 0) {
        if (headsDiv) headsDiv.remove();
        return;
    }

    if (!headsDiv) {
        headsDiv = document.createElement('div');
        headsDiv.className = 'mc-player-heads';
        container.appendChild(headsDiv);
    }

    const newUuids = new Set(players.map((p) => p.uuid));

    // Existing heads keyed by UUID
    const existingHeads = new Map<string, HTMLImageElement>();
    headsDiv.querySelectorAll<HTMLImageElement>('.mc-player-head').forEach((img) => {
        const uuid = img.dataset.uuid;
        if (uuid) {
            existingHeads.set(uuid, img);
        }
    });

    // Remove players no longer present -- removes the wrapping profile anchor too, not just the
    // <img>, when the head is currently wrapped in one (Plan 06's per-head .mc-player-head-link).
    for (const [uuid, img] of existingHeads) {
        if (!newUuids.has(uuid)) {
            (img.closest<HTMLElement>('.mc-player-head-link') ?? img).remove();
        }
    }

    // Phase 239 D-13/D-14/D-15 -- the server key comes from the card's existing data-server-key
    // attribute (read once via the container's ancestor .mc-card-col), never a new data attribute
    // and never threaded through the poll payload itself.
    const serverKey = container.closest<HTMLElement>('.mc-card-col')?.dataset.serverKey ?? '';

    // Add new players, and REFRESH the ones already on the card.
    //
    // Phase 240 D-17 -- until now this loop only ever ran its create branch, so a player who was
    // already on the card kept the tooltip and the ring they were server-rendered with at page
    // load, frozen, however far their vitals drifted over the following ticks. The update branch
    // below closes that; the create branch's enriched tooltip closes the other half (a player who
    // joins between page load and a tick used to get a bare name where the server would have
    // rendered the full composition).
    for (const player of players) {
        // D-14/D-16 extended to this second mirror (SC6). Runs BEFORE either branch touches the
        // DOM, so an unreadable payload leaves an existing avatar exactly as the server rendered
        // it and simply declines to invent a new one -- stale-but-complete beats fresh-but-gutted.
        if (!reconcileFleetPlayerFields(player as unknown as Record<string, unknown>)) {
            continue;
        }

        // T-231-07/T-240-30: both sinks below are PROPERTY assignments on elements built with
        // createElement -- the composed string is never interpolated into markup, and the class
        // string is a literal plus a boolean-selected literal, so no wire value reaches it.
        const existing = existingHeads.get(player.uuid);
        if (existing) {
            // Re-set both every tick rather than diffing: it is cheap, and assigning the whole
            // className (never toggling one piece of a hand-built string) is what keeps this path
            // from drifting away from the create path below.
            existing.title = buildFleetTooltip(player);
            existing.className = 'mc-player-head rounded-sm'
                + (player.isLowHealth ? ' mc-player-head--low-health' : '');
        } else {
            const img = document.createElement('img');
            img.src = `https://mc-heads.net/avatar/${player.uuid}/16`;
            img.loading = 'lazy';
            img.width = 16;
            img.height = 16;
            // Phase 239 D-13/D-04 -- alt/title route through the server-resolved display_name,
            // never a raw name or uuid (the static guard at tools/check-mc-uuid-leak-static.mjs
            // forbids `.name` reaching either sink). `alt` stays the bare name: it is an image
            // alternative, not a tooltip, and default.latte's own alt is the bare name too.
            img.alt = player.display_name;
            img.title = buildFleetTooltip(player);
            img.className = 'mc-player-head rounded-sm'
                + (player.isLowHealth ? ' mc-player-head--low-health' : '');
            img.dataset.uuid = player.uuid;

            // D-14: a head is linked ONLY when a profile actually exists (player.linkable, fails
            // closed to false) -- reproduces default.latte's per-head anchor exactly, a genuine
            // sibling <a> of the card's stretched-link title anchor, never nested inside it
            // [LOCKED STRUCTURAL DECISION] -- no JS click handler, no event bubble-cancelling call
            // of any kind.
            if (player.linkable) {
                const link = document.createElement('a');
                link.href = `/mc/players/${encodeURIComponent(player.uuid)}?server=${encodeURIComponent(serverKey)}`;
                link.title = 'View profile';
                link.className = 'mc-player-head-link';
                link.appendChild(img);
                headsDiv.appendChild(link);
            } else {
                headsDiv.appendChild(img);
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Card reorder (Phase 257, D-04) -- online cluster first by player count desc, then offline
// by last seen desc with an alphabetical tiebreak, never-seen last. The rule itself lives in
// src/js/utils/serverSort.ts's compareServers(), which mirrors App\Mc\ServerSort::compare()
// exactly -- changing one without the other reintroduces the one-tick reshuffle this phase
// closed: the initial server-rendered order and the first poll-tick reorder would disagree.
// ---------------------------------------------------------------------------

function reorderCards(servers: ServerData[]): void {
    const sorted = [...servers].sort(compareServers);

    sorted.forEach((server, i) => {
        const col = document.querySelector<HTMLElement>(
            `.mc-card-col[data-server-key="${server.key}"]`,
        );
        if (col) {
            col.style.order = String(i);
        }
    });
}

// ---------------------------------------------------------------------------
// Summary stats
// ---------------------------------------------------------------------------

function updateSummary(servers: ServerData[]): void {
    const el = document.getElementById('mc-summary');
    if (!el) return;

    let onlineCount = 0;
    let totalPlayers = 0;

    for (const s of servers) {
        if (s.isOnline) {
            onlineCount++;
            totalPlayers += s.onlinePlayers ?? 0;
        }
    }

    el.textContent = `${onlineCount}/${servers.length} online \u2014 ${totalPlayers} players`;
}

// ---------------------------------------------------------------------------
// Detail page
// ---------------------------------------------------------------------------

let detailPollTimer: ReturnType<typeof setTimeout> | null = null;

// WR-01: keep the McConsole instance so destroy() can be called before
// re-init, removing the visibilitychange listener from the prior instance.
let mcConsoleInstance: McConsole | null = null;

function initDetail(container: HTMLElement): void {
    const statusUrl = container.dataset.statusUrl ?? '';
    // D-07: no cron polls an archived server, so a live-vitals block would present stale data as
    // current -- read ONCE at init from the server-rendered data-archived attribute (detail.latte's
    // .mc-detail root, 238-10), NEVER re-derived from a poll response, since an archived server
    // gets no poll response by design. This file has a documented history of silently diverging
    // from detail.latte (Phase 235's poll-tick regression, hotfixed pre-238); Phase 240 owns the
    // lockstep guard, so this deliberately keeps the divergence surface to this single attribute
    // read rather than adding a parallel client-side state machine.
    const isArchived = container.dataset.archived === '1';

    initCopyButton(container);
    initBluemap(container);
    // Phase 230 D-06/D-07/D-08: idempotent -- controls.ts guards its own double-init.
    initControls(container);

    // Initialize console if section exists (auth-gated)
    const consoleSection = container.querySelector<HTMLElement>('.mc-console-section');
    if (consoleSection) {
        // WR-01: destroy prior instance before creating a new one to prevent
        // listener + socket leaks on SPA-style re-renders of the detail section.
        if (mcConsoleInstance !== null) {
            mcConsoleInstance.destroy();
        }
        mcConsoleInstance = new McConsole(consoleSection);
    }

    // Phase 229 (HEALTH-02): resource-history chart. initDetail() runs once per full page load
    // (not per poll tick), and initResourceHistory() itself guards against double-init on the
    // same container -- do NOT call this from pollDetail()/updateDetail(), which would re-create
    // the Highcharts instance every 30s.
    initResourceHistory(container);

    // Phase 251 (251-08): the Chat section renders regardless of archived status (D-02/D-05 —
    // it is publicly viewable and the operator's own broadcasts still render for an archived
    // server; only the compose form is separately gated in detail.latte). Self-guards on
    // .mc-chatlog-section presence and against double-init, matching initFeed()/initHeatmap().
    initChatlog(container);

    // Phase 253 (253-05, D-07): Player Roster click-to-sort headers. The roster is an all-time
    // cumulative read, meaningful even for an archived server, so this must initialise BEFORE the
    // archived early-return below rather than being gated by it. Self-guards on .mc-roster-table
    // presence and against double-init, matching initChatlog()/initResourceHistory().
    initRoster(container);

    // D-07: combined into this ONE gate rather than a second early return further down, so there
    // is exactly one place the detail poll can be prevented from starting.
    if (!statusUrl || isArchived) return;

    // Start polling
    const scheduleDetailPoll = (): void => {
        detailPollTimer = setTimeout(() => pollDetail(container, statusUrl, scheduleDetailPoll), POLL_INTERVAL);
    };

    scheduleDetailPoll();

    // Visibility API: pause when hidden, immediate fetch on return
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            if (detailPollTimer !== null) {
                clearTimeout(detailPollTimer);
                detailPollTimer = null;
            }
        } else {
            pollDetail(container, statusUrl, scheduleDetailPoll);
        }
    });
}

async function pollDetail(
    container: HTMLElement,
    statusUrl: string,
    reschedule: () => void,
): Promise<void> {
    if (detailPollTimer !== null) {
        clearTimeout(detailPollTimer);
        detailPollTimer = null;
    }

    try {
        const resp = await fetch(statusUrl, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!resp.ok) {
            console.error(`[mc] Detail poll failed: ${resp.status}`);
        } else {
            const data: DetailServerData = await resp.json();
            updateDetail(container, data);
        }

        // Phase 229 (HEALTH-02, D-07): tail-fetch + live-append the resource-history chart's 24h
        // raw range from the samples table on this SAME poll cycle -- a separate signal
        // (McPresenter::handleGetResourceTail) from the status-cache fetch above, so it still
        // runs even if that fetch failed. Guarded on the chart existing on this page; the chart
        // instance itself is created only in initDetail() (never here) -- appendLatest() only
        // ever calls series.addPoint(), never Highcharts.chart().
        if (container.querySelector('#mc-resource-history-chart')) {
            void appendLatestResourceHistory();
        }
    } catch (err) {
        console.error('[mc] Detail poll error:', err);
    }

    reschedule();
}

function updateDetail(container: HTMLElement, data: DetailServerData): void {
    // Status badge -- isSuspended checked FIRST (Phase 230 D-05): a suspended server never flips
    // to the plain Online/Offline badge on a live poll tick.
    const badge = container.querySelector<HTMLElement>('h3 .badge');
    if (badge) {
        if (data.isSuspended) {
            badge.classList.remove('bg-success', 'bg-danger', 'bg-warning', 'text-dark');
            badge.classList.add('bg-dark', 'text-white');
            badge.textContent = 'Suspended';
        } else {
            badge.classList.remove('bg-dark', 'text-white', 'bg-success', 'bg-danger');
            badge.classList.add(data.isOnline ? 'bg-success' : 'bg-danger');
            badge.textContent = data.isOnline ? 'Online' : 'Offline';
        }
    }

    // MOTD -- DELETED, CR-03 (240-REVIEW). Same dead-but-armed raw-HTML sink as the fleet card's
    // (updateGrid() above): de91668f removed `.mc-motd` from detail.latte too, so
    // `container.querySelector('.mc-motd')` has been null on every tick since. See the longer
    // note at the fleet-card site for the "if MOTD ever comes back" guidance.

    // Version -- CR-03 (240-REVIEW). `data.version` is whatever string the REMOTE Minecraft
    // server puts in its SLP/ping response (ParallelPinger reads `version.name`, ApiPinger reads
    // mcsrvstat's `version`); this application never validates it and `/mc/<key>` has no auth
    // redirect (BasePresenter::load()), so the `parent.innerHTML = \`<strong>Version:</strong>
    // ${data.version}\`` that stood here was a DOM-XSS against every anonymous visitor, fired on
    // the 30s poll tick. Rebuilt from elements + a text node instead -- T-231-07, textContent
    // only, never innerHTML. `append(node, string)` appends the string as a Text node, so no
    // escaping helper is needed (and no fourth module-private escapeHtml() copy is created).
    const versionEl = container.querySelector<HTMLElement>('.mc-detail-info .mb-2 strong');
    if (versionEl && versionEl.textContent === 'Version:') {
        const parent = versionEl.parentElement;
        if (parent) {
            const label = document.createElement('strong');
            label.textContent = 'Version:';
            parent.replaceChildren(label, ` ${data.version ?? 'Unknown'}`);
        }
    }

    // Presence source badge (Phase 231 D-04/D-05) -- checked ahead of the player
    // count/latency/list updates below since presenceSource resolves independently of isOnline.
    updateDetailPresenceSource(container, data);

    // Player count
    updateDetailPlayerCount(container, data);

    // Latency
    updateDetailLatency(container, data);

    // Favicon
    const iconImg = container.querySelector<HTMLImageElement>('.mc-detail-icon img');
    if (iconImg && data.favicon) {
        iconImg.src = data.favicon;
    }

    // Player list
    updateDetailPlayerList(container, data);

    // Quick-task follow-up to Phase 253: Today snapshot + Player Roster now live-update on this
    // same 30s tick, matching Players/Resource History. Both self-guard on their own section
    // markup and are no-ops on an archived page (this whole poll never fires there — see
    // initDetail()'s D-07 gate).
    updateToday(container, data.today, data.key);
    updateRosterRows(container, data.roster, data.key);

    // Last seen (offline)
    updateDetailLastSeen(container, data);

    // Connection address (defensive)
    const addressCode = container.querySelector<HTMLElement>('.mc-address code');
    if (addressCode && data.address) {
        addressCode.textContent = data.address;
    }
    // Phase 269: scoped to `.mc-address .mc-copy-btn` specifically, NOT the bare `.mc-copy-btn`
    // class -- a second `.mc-copy-btn` (the world seed, inside `#mc-world-accordion`) now exists
    // in the same container, and its data-copy must never be overwritten with the server address
    // on a poll tick (the seed does not change between polls).
    const copyBtn = container.querySelector<HTMLElement>('.mc-address .mc-copy-btn');
    if (copyBtn && data.address) {
        copyBtn.setAttribute('data-copy', data.address);
    }

    // Phase 230 D-05: power/command controls disable live, re-evaluated on every poll tick
    // (mirrors the badge swap above and updateSuspendedBadge()'s create/remove idiom).
    updateControlsSuspended(container, data.isSuspended);
}

// ---------------------------------------------------------------------------
// Today section live update -- quick-task follow-up to Phase 253
// ---------------------------------------------------------------------------

/**
 * Reconciles the Today section against a fresh poll payload, matching detail.latte's own
 * three-way branch (analytics id absent / rows empty / rows present) exactly. `today === null`
 * means this server has no analytics id at all — a fact that cannot change between polls, so
 * there is nothing to reconcile and the section (which the server never rendered in the first
 * place) is left untouched. Rebuilds every `.mc-today-row` from scratch each tick, matching
 * `updateRosterRows()`'s own "cheap to fully rebuild, no uuid-diffing" reasoning — this fleet's
 * per-server today-activity count runs a handful of players at most.
 */
// Phase 273 Plan 13 -- exported (was module-private) so tools/probe-273-13-js-parity.mjs can drive
// a REAL simulated poll tick against a REAL headless-Chromium DOM, mirroring roster.ts's own
// already-exported updateRosterRows() (Plan 273-10's identical precedent). No behavior change.
export function updateToday(container: HTMLElement, today: TodayPayload | null, serverKey: string): void {
    if (today === null) return;

    const section = container.querySelector<HTMLElement>('.mc-today-section');
    if (!section) return;

    const note = section.querySelector<HTMLElement>('.mc-today-boundary-note');
    if (note) {
        note.textContent = today.window_boundary_note;
    }

    section.querySelectorAll('.mc-today-row').forEach((el) => el.remove());
    const emptyMsg = section.querySelector('p.text-muted');

    if (today.rows.length === 0) {
        if (!emptyMsg) {
            const p = document.createElement('p');
            p.className = 'text-muted';
            p.textContent = 'No activity yet today.';
            section.appendChild(p);
        }
        return;
    }

    emptyMsg?.remove();

    for (const row of today.rows) {
        section.appendChild(buildTodayRow(row, serverKey));
    }
}

/** One `.mc-today-row`, matching detail.latte:533-568's Today row markup exactly (the same eight
 *  mcValueCell stats in the same locked UI-SPEC order -- Active Playtime now leads Playtime,
 *  Player Kills deliberately excluded — D-04, not an oversight).
 *
 *  Phase 273 D-09/AFK-03 (Plan 13): the stats tuple is widened from three elements to five --
 *  [label, value, unit, absenceTitle, dataMetric] -- rather than a parallel lookup, so the loop
 *  stays a single pass and each entry's own reason/selector sit next to its value at the
 *  declaration site (easiest place to keep the two new columns from drifting apart per-row).
 *  Only the Active entry carries a non-null `absenceTitle`; every other entry passes `null`,
 *  which fillMcValueCell()'s own default parameter already treats identically to omitting the
 *  argument (falls back to MC_VALUE_NOT_MEASURED_TITLE). `dataMetric` is set on the Active and
 *  Playtime entries only, matching detail.latte's own scoping decision -- no other entry carries
 *  a `data-metric` attribute. */
function buildTodayRow(row: TodayRow, serverKey: string): HTMLElement {
    const div = document.createElement('div');
    div.className = 'mc-today-row';
    div.appendChild(buildMcAvatarTile(row.uuid));

    const nameCol = document.createElement('span');
    nameCol.className = 'd-flex flex-column';
    nameCol.appendChild(buildMcNameWrap(row, serverKey));

    const statsSpan = document.createElement('span');
    statsSpan.className = 'mc-today-stats';

    const stats: Array<[string, number | null, string, string | null, string | null]> = [
        ['Active Playtime', row.active_playtime, 'duration', row.active_playtime_reason, 'active_playtime'],
        ['Playtime', row.playtime, 'duration', null, 'playtime'],
        ['Blocks Mined', row.blocks_mined, 'count', null, null],
        ['Ground', row.ground, 'distance_cm', null, null],
        ['Water', row.water, 'distance_cm', null, null],
        ['Air & Vehicle', row.air_vehicle, 'distance_cm', null, null],
        ['Mob Kills', row.mob_kills, 'count', null, null],
        ['Deaths', row.deaths, 'count', null, null],
    ];
    for (const [label, value, unit, absenceTitle, dataMetric] of stats) {
        const statSpan = document.createElement('span');
        statSpan.className = 'mc-today-stat';
        if (dataMetric !== null) {
            statSpan.dataset.metric = dataMetric;
        }

        const labelSpan = document.createElement('span');
        labelSpan.className = 'text-muted';
        labelSpan.textContent = label;
        statSpan.append(labelSpan, ' ');

        const valueSpan = document.createElement('span');
        valueSpan.className = 'mc-board-value';
        fillMcValueCell(valueSpan, value, unit, row.baseline_incomplete, absenceTitle);
        statSpan.appendChild(valueSpan);

        statsSpan.appendChild(statSpan);
    }
    nameCol.appendChild(statsSpan);

    div.appendChild(nameCol);
    return div;
}

// ---------------------------------------------------------------------------
// Controls suspended-gate (Phase 230 D-05) -- detail page
// ---------------------------------------------------------------------------

function updateControlsSuspended(container: HTMLElement, isSuspended: boolean): void {
    const controls = container.querySelector<HTMLElement>('.mc-console-controls');
    if (!controls) return; // not logged in -- controls markup doesn't exist on the page

    controls
        .querySelectorAll<HTMLButtonElement | HTMLInputElement>(
            '.mc-power-btn, .mc-command-input, .mc-command-send',
        )
        .forEach((el) => {
            el.disabled = isSuspended;
        });

    let note = controls.querySelector<HTMLElement>('.mc-controls-suspended-note');
    if (isSuspended) {
        if (!note) {
            // Create the note if it doesn't exist (server just became suspended this tick) --
            // mirrors updateSuspendedBadge()'s create/remove pattern. Fixed literal text, no
            // server-controlled data interpolated.
            note = document.createElement('div');
            note.className = 'mc-controls-suspended-note mb-2';
            note.textContent = 'Power and command controls are disabled while this server is suspended.';
            controls.prepend(note);
        }
    } else if (note) {
        note.remove();
    }
}

// ---------------------------------------------------------------------------
// Presence source badge (Phase 231 D-04/D-05) -- detail page
// ---------------------------------------------------------------------------

function updateDetailPresenceSource(container: HTMLElement, data: DetailServerData): void {
    const infoPanel = container.querySelector('.mc-detail-info');
    if (!infoPanel) return;

    let row = infoPanel.querySelector<HTMLElement>('.mc-presence-source-row');

    if (data.presenceSource !== 'live' && data.presenceSource !== 'ping') {
        // null -- pre-231 payload / resolver didn't run. Remove any existing badge rather than
        // let it show a stale source once the discriminator itself goes missing (D-05 honesty).
        if (row) row.remove();
        return;
    }

    if (!row) {
        // Create the row + badge if it doesn't exist yet (mirrors updateSuspendedBadge()'s /
        // updateDetailPlayerCount()'s find-or-create idiom). Inserted in the SAME DOM position
        // detail.latte server-renders it: directly after the h3 status-badge cluster, before any
        // suspended/stale/last-seen notice that might already exist (or be freshly created this
        // same tick).
        const heading = infoPanel.querySelector('h3');
        if (!heading) return;

        row = document.createElement('div');
        row.className = 'mc-presence-source-row mb-2';
        const badgeEl = document.createElement('span');
        badgeEl.className = 'badge mc-presence-source';
        row.appendChild(badgeEl);

        const nextNotice = infoPanel.querySelector(
            '.mc-suspended-notice, .mc-stale-notice, .mc-detail-last-seen',
        );
        if (nextNotice) {
            nextNotice.before(row);
        } else {
            heading.after(row);
        }
    }

    const badge = row.querySelector<HTMLElement>('.mc-presence-source');
    if (!badge) return;

    // T-231-10: the class is chosen from a fixed live/ping allow-list literal, never
    // interpolated from server-controlled data.
    badge.classList.remove('mc-presence-source--live', 'mc-presence-source--ping');
    badge.classList.add(
        data.presenceSource === 'live' ? 'mc-presence-source--live' : 'mc-presence-source--ping',
    );
    // T-231-07: textContent only, never innerHTML, even for these fixed literal strings.
    badge.textContent = data.presenceSource === 'live' ? 'Live' : 'Ping';
    badge.title = data.presenceSource === 'live'
        ? 'Presence from live heartbeat telemetry'
        : 'Presence from server ping';
}

function updateDetailPlayerCount(container: HTMLElement, data: DetailServerData): void {
    // Find or create player count element
    const infoPanel = container.querySelector('.mc-detail-info');
    if (!infoPanel) return;

    let playerCountEl = infoPanel.querySelector<HTMLElement>('.mc-detail-player-count');

    if (data.isOnline) {
        if (!playerCountEl) {
            playerCountEl = document.createElement('div');
            playerCountEl.className = 'mb-2 mc-detail-player-count';
            // Insert after latency or version
            const latencyEl = infoPanel.querySelector('.mc-detail-latency');
            const ref = latencyEl ?? infoPanel.querySelector('.mc-address');
            if (ref) {
                ref.before(playerCountEl);
            } else {
                infoPanel.appendChild(playerCountEl);
            }
        }
        // CR-03 (240-REVIEW): rebuilt from elements for the same reason the Version row above was.
        // `onlinePlayers`/`maxPlayers` are `?int` on ServerStatus and `(int)`-cast in McModel and
        // both pingers, so this template literal is NOT attacker-controlled today -- but it is the
        // identical shape to the Version sink, one type-widening away from being one, and leaving
        // it would mean this file still carried an `innerHTML =` with an interpolated wire value.
        const playersLabel = document.createElement('strong');
        playersLabel.textContent = 'Players:';
        playerCountEl.replaceChildren(playersLabel, ` ${data.onlinePlayers}/${data.maxPlayers}`);
    } else if (playerCountEl) {
        playerCountEl.remove();
    }
}

function updateDetailLatency(container: HTMLElement, data: DetailServerData): void {
    const infoPanel = container.querySelector('.mc-detail-info');
    if (!infoPanel) return;

    let latencyEl = infoPanel.querySelector<HTMLElement>('.mc-detail-latency');

    if (data.isOnline && data.latency !== null) {
        if (!latencyEl) {
            latencyEl = document.createElement('div');
            latencyEl.className = 'mb-2 mc-detail-latency';
            const addressEl = infoPanel.querySelector('.mc-address');
            if (addressEl) {
                addressEl.before(latencyEl);
            } else {
                infoPanel.appendChild(latencyEl);
            }
        }

        const cls = data.latency < 50 ? 'mc-latency--good'
            : data.latency <= 150 ? 'mc-latency--ok'
                : 'mc-latency--bad';
        // CR-03 (240-REVIEW): rebuilt from elements. `Math.round()` is a total function to number,
        // so the old template literal could not have carried markup even for a hostile `latency` --
        // this one is hardening for the grep-checkable invariant ("no `innerHTML =` with an
        // interpolated expression anywhere in mc.ts"), not a live hole. `cls` comes from the fixed
        // three-literal ternary directly above, never from wire data.
        const latencyLabel = document.createElement('strong');
        latencyLabel.textContent = 'Latency:';
        const latencyValue = document.createElement('span');
        latencyValue.className = `mc-latency ${cls}`;
        latencyValue.textContent = `${Math.round(data.latency)}ms`;
        latencyEl.replaceChildren(latencyLabel, ' ', latencyValue);
    } else if (latencyEl) {
        latencyEl.remove();
    }
}

function updateDetailPlayerList(container: HTMLElement, data: DetailServerData): void {
    const playersSection = container.querySelector<HTMLElement>('.mc-detail-players');
    if (!playersSection) return;

    // Phase 231 (LIVE-02/D-02) -- a 'live' (push/heartbeat) server forks to rich per-player rows
    // carrying dimension + biome, AFK dimmed/tagged, mirroring detail.latte's own server-rendered
    // fork exactly. Checked FIRST, ahead of the isOnline/offline branches below, since
    // presenceSource resolves independently of isOnline (this poll payload carries no isStale/
    // lastPlayers fields -- those are template-render-only, pre-231, out of this plan's scope).
    if (data.presenceSource === 'live') {
        renderDetailLivePlayerRows(playersSection, data);
        return;
    }

    if (data.isOnline) {
        // Update header
        const header = playersSection.querySelector('h5');
        if (header) header.textContent = `Players (${data.onlinePlayers})`;

        // Remove "Server is offline" message if present
        const offlineMsg = playersSection.querySelector('p.text-muted');
        if (offlineMsg && offlineMsg.textContent === 'Server is offline') {
            offlineMsg.remove();
        }

        // Get or clear existing player rows (matches both plain AND rich rows -- a server
        // flipping from 'live' back to 'ping'/null mid-poll must not leave stale --rich rows
        // behind, since '.mc-detail-player' matches 'mc-detail-player mc-detail-player--rich' too)
        const existingPlayers = playersSection.querySelectorAll('.mc-detail-player');
        existingPlayers.forEach((el) => el.remove());

        // Remove existing "No players online" message
        const noPlayersMsg = playersSection.querySelector('p.text-muted');
        if (noPlayersMsg) noPlayersMsg.remove();

        if (data.players.length > 0) {
            for (const player of data.players) {
                const row = document.createElement('div');
                row.className = 'mc-detail-player d-flex align-items-center gap-2 mb-2';

                // GAP-230 WR-01 / Phase 239 D-13/D-04: player.uuid is server-controlled (SLP
                // sample list) -- build via createElement + property assignment, never innerHTML,
                // mirroring diffPlayerHeads() above. The avatar is decorative (alt="") since the
                // name span below is now both the accessible label and (when linkable) the
                // anchor, matching detail.latte's plain online-player branch exactly.
                const img = document.createElement('img');
                img.src = `https://mc-heads.net/avatar/${player.uuid}/32`;
                img.loading = 'lazy';
                img.width = 32;
                img.height = 32;
                img.alt = '';
                img.className = 'mc-player-avatar rounded';
                img.addEventListener('error', () => {
                    img.style.display = 'none';
                });

                const nameWrap = document.createElement('span');

                const nameSpan = document.createElement('span');
                nameSpan.textContent = player.display_name;
                if (player.unresolved) {
                    nameSpan.className = 'mc-board-name--unresolved';
                }

                // D-14: a name is linked ONLY when a profile actually exists (player.linkable,
                // fails closed to false) -- an absent link, never a dead one.
                if (player.linkable) {
                    const link = document.createElement('a');
                    link.href = `/mc/players/${encodeURIComponent(player.uuid)}?server=${encodeURIComponent(data.key)}`;
                    link.title = 'View profile';
                    link.appendChild(nameSpan);
                    nameWrap.appendChild(link);
                } else {
                    nameWrap.appendChild(nameSpan);
                }

                if (player.unresolved) {
                    const resolvingBadge = document.createElement('span');
                    resolvingBadge.className = 'mc-identity-badge mc-identity-badge--resolving';
                    resolvingBadge.title = 'This player’s current Minecraft name hasn’t been resolved yet. The '
                        + 'placeholder is built from their UUID so it can never be mistaken for a real name.';
                    resolvingBadge.textContent = 'Resolving…';
                    nameWrap.appendChild(resolvingBadge);
                }

                if (!player.is_online_mode) {
                    const offlineBadge = document.createElement('span');
                    offlineBadge.className = 'mc-identity-badge mc-identity-badge--offline';
                    offlineBadge.title = 'This UUID wasn’t issued by Mojang (likely an offline-mode/cracked '
                        + 'account). Ranked normally — never merged with or split from a real identity.';
                    offlineBadge.textContent = 'Non-Mojang ID';
                    nameWrap.appendChild(offlineBadge);
                }

                row.appendChild(img);
                row.appendChild(nameWrap);
                playersSection.appendChild(row);
            }
        } else {
            const msg = document.createElement('p');
            msg.className = 'text-muted';
            msg.textContent = 'No players online';
            playersSection.appendChild(msg);
        }
    } else {
        // Offline state
        const header = playersSection.querySelector('h5');
        if (header) header.textContent = 'Players';

        const existingPlayers = playersSection.querySelectorAll('.mc-detail-player');
        existingPlayers.forEach((el) => el.remove());

        const noPlayersMsg = playersSection.querySelector('p.text-muted');
        if (noPlayersMsg) {
            noPlayersMsg.textContent = 'Server is offline';
        } else {
            const msg = document.createElement('p');
            msg.className = 'text-muted';
            msg.textContent = 'Server is offline';
            playersSection.appendChild(msg);
        }
    }
}

// Phase 231 (LIVE-02) -- rich per-player row rendering for a 'live' server, mirroring
// detail.latte's server-rendered `mc-detail-player--rich` markup exactly (name + AFK tag,
// dimension/biome humanized and middle-dot joined, AFK dimming via the same hardcoded
// style.opacity idiom the shipped template uses -- never a CSS-context Latte/JS-interpolated
// value, per this project's own float-escaping scar).
//
// Quick-task 260729-hy2 (Phase 235 live-poll regression fix) -- widened to mirror
// detail.latte lines 226-291 in order and in guard structure: name line, vitals line
// (health/food/ping), dimension/biome line, effects row. `hasHealth` is derived ONCE and reused
// in every guard below, exactly as the template reuses its own `$hasHealth` -- never re-derived
// inline. T-231-07 is non-negotiable and remains fully honoured: every server-controlled string
// (name, uuid, dimension, biome, healthLabel, foodTitle, effect label/title) reaches the DOM via
// `textContent` or a direct property assignment, never raw-markup assignment.
//
// Phase 240 D-14/D-16 (240-07) -- the row lifecycle is now PER-PLAYER and guarded. Each payload
// entry is checked against the declared key set in `./utils/detailPollFields` BEFORE its row is
// touched; an entry carrying a key this function does not handle causes that player's row to be
// left exactly as `detail.latte` rendered it, while every other player still refreshes. Both render
// paths stamp `data-uuid` on the row so a preserved row is still addressable on the next tick.
function renderDetailLivePlayerRows(playersSection: HTMLElement, data: DetailServerData): void {
    const header = playersSection.querySelector('h5');
    if (header) header.textContent = `Players (${data.onlinePlayers})`;

    // Remove any stale "No players online" / "Server is offline" message.
    const staleMsg = playersSection.querySelector('p.text-muted');
    if (staleMsg) staleMsg.remove();

    // Phase 240 D-16 / 240-RESEARCH Pitfall 6 -- the row-clear is PER-PLAYER now, not a single
    // up-front sweep of every row. The old sweep removed all rows before the per-player loop even
    // started, which would have destroyed the very row the reconciliation guard below exists to
    // preserve: "keep the server-rendered row" is only keepable if the row is still there when the
    // guard runs. Index what is in the DOM first, remove nothing yet.
    const existingRows = new Map<string, Element>();
    const legacyRows: Element[] = [];
    playersSection.querySelectorAll('.mc-detail-player').forEach((el) => {
        const rowUuid = (el as HTMLElement).dataset.uuid;
        if (rowUuid) {
            existingRows.set(rowUuid, el);
        } else {
            // A plain row from the 'ping'/null presence branch carries no identity attribute --
            // a flip from plain to rich must still replace it, exactly as the old sweep did.
            legacyRows.push(el);
        }
    });
    for (const el of legacyRows) el.remove();

    // "Player left" -- rows whose uuid is absent from this tick's payload. Removed up front
    // because no per-player guard can ever run for a player the server no longer reports.
    const incomingUuids = new Set(data.players.map((p) => p.uuid));
    for (const [rowUuid, el] of existingRows) {
        if (!incomingUuids.has(rowUuid)) {
            el.remove();
            existingRows.delete(rowUuid);
        }
    }

    if (data.players.length === 0) {
        const msg = document.createElement('p');
        msg.className = 'text-muted';
        msg.textContent = 'No players online';
        playersSection.appendChild(msg);
        return;
    }

    for (const player of data.players) {
        // Phase 240 D-14/D-16 -- FIRST, before this player's row is removed or rebuilt. On an
        // unrecognized payload key this renderer provably does not understand the wire, so the
        // server-rendered row is left EXACTLY as it is and every other player in the same tick
        // still rebuilds normally. Stale-but-complete beats fresh-but-gutted. The guard reports to
        // both a console line and a headless-readable window marker (see detailPollFields.ts).
        if (!reconcilePlayerFields(player as unknown as Record<string, unknown>)) {
            continue;
        }

        // Only now is this player's own previous row disposable -- the guard passed, so the
        // rebuild below is known to be a faithful re-render, not a degradation.
        const previousRow = existingRows.get(player.uuid);
        if (previousRow) {
            previousRow.remove();
            existingRows.delete(player.uuid);
        }

        const row = document.createElement('div');
        row.className = 'mc-detail-player mc-detail-player--rich d-flex align-items-center gap-2 mb-2';
        // Phase 240 D-16 -- the same identity attribute detail.latte stamps on its server-rendered
        // row, so both render paths are addressable the same way and a preserved row survives the
        // NEXT tick's lookup too.
        row.dataset.uuid = player.uuid;
        // AFK dimming -- hardcoded style property assignment, never a server string interpolated
        // into a CSS-context attribute (T-231-07).
        if (player.afk) {
            row.style.opacity = '0.5';
        }

        // The single derived boolean every guard below reuses -- mirrors detail.latte's own
        // `$hasHealth`, computed once and never re-derived inline.
        const hasHealth = player.healthLabel != null;

        // T-231-07 / Phase 239 D-13/D-04: player.uuid/dimension/biome are server-controlled
        // (heartbeat sample) -- build via createElement + textContent/property assignment only,
        // no raw-markup assignment. The avatar is decorative (alt="") since the name span below
        // is now both the accessible label and (when linkable) the anchor, matching detail.latte's
        // rich/live online-player branch exactly.
        const avatarImg = document.createElement('img');
        avatarImg.src = `https://mc-heads.net/avatar/${player.uuid}/32`;
        avatarImg.loading = 'lazy';
        avatarImg.width = 32;
        avatarImg.height = 32;
        avatarImg.alt = '';
        avatarImg.className = 'mc-player-avatar rounded' + (player.isLowHealth ? ' mc-player-head--low-health' : '');
        avatarImg.addEventListener('error', () => {
            avatarImg.style.display = 'none';
        });

        const nameCol = document.createElement('span');
        nameCol.className = 'd-flex flex-column';

        const nameLine = document.createElement('span');

        const nameSpan = document.createElement('span');
        nameSpan.textContent = player.display_name;
        if (player.unresolved) {
            nameSpan.className = 'mc-board-name--unresolved';
        }

        // D-14: a name is linked ONLY when a profile actually exists (player.linkable, fails
        // closed to false) -- an absent link, never a dead one.
        if (player.linkable) {
            const link = document.createElement('a');
            link.href = `/mc/players/${encodeURIComponent(player.uuid)}?server=${encodeURIComponent(data.key)}`;
            link.title = 'View profile';
            link.appendChild(nameSpan);
            nameLine.appendChild(link);
        } else {
            nameLine.appendChild(nameSpan);
        }

        if (player.unresolved) {
            const resolvingBadge = document.createElement('span');
            resolvingBadge.className = 'mc-identity-badge mc-identity-badge--resolving';
            resolvingBadge.title = 'This player’s current Minecraft name hasn’t been resolved yet. The '
                + 'placeholder is built from their UUID so it can never be mistaken for a real name.';
            resolvingBadge.textContent = 'Resolving…';
            nameLine.appendChild(resolvingBadge);
        }

        if (!player.is_online_mode) {
            const offlineBadge = document.createElement('span');
            offlineBadge.className = 'mc-identity-badge mc-identity-badge--offline';
            offlineBadge.title = 'This UUID wasn’t issued by Mojang (likely an offline-mode/cracked '
                + 'account). Ranked normally — never merged with or split from a real identity.';
            offlineBadge.textContent = 'Non-Mojang ID';
            nameLine.appendChild(offlineBadge);
        }

        if (player.afk) {
            const afkTag = document.createElement('span');
            afkTag.className = 'badge bg-secondary ms-1';
            afkTag.textContent = 'AFK';
            nameLine.appendChild(afkTag);
        }
        nameCol.appendChild(nameLine);

        // Vitals line -- health/food/ping, omitted entirely when all three are absent. Every
        // separator guard below reads the SAME `hasHealth`/`player.food`/`player.ping` checks in
        // the SAME order detail.latte uses, never a re-derived condition.
        if (hasHealth || player.food != null || player.ping != null) {
            const vitalsEl = document.createElement('span');
            vitalsEl.className = 'mc-player-vitals';

            if (hasHealth) {
                const healthEl = document.createElement('span');
                healthEl.className = 'mc-player-health' + (player.isLowHealth ? ' mc-player-health--low' : '');
                // U+2764 HEAVY BLACK HEART -- the literal character, matching what the browser's
                // HTML parser decodes the template's own `&#10084;` entity into in the live DOM.
                healthEl.textContent = `❤ ${player.healthLabel}`;
                vitalsEl.appendChild(healthEl);
            }
            if (hasHealth && player.food != null) {
                // ' · ' not '·' -- detail.latte emits `&middot;` on its own source line, so the
                // HTML parser collapses the surrounding indentation to one space on each side.
                // A bare '·' renders visibly tighter than the server-rendered row it must match.
                vitalsEl.appendChild(document.createTextNode(' · '));
            }
            if (player.food != null) {
                const foodEl = document.createElement('span');
                foodEl.className = 'mc-player-food';
                if (player.foodTitle != null) {
                    foodEl.title = player.foodTitle;
                }
                // U+1F357 POULTRY LEG -- the literal character, matching `&#127831;`'s decoded form.
                foodEl.textContent = `\u{1F357} ${player.food}/20`;
                vitalsEl.appendChild(foodEl);
            }
            if ((hasHealth || player.food != null) && player.ping != null) {
                // ' · ' -- same collapsed-whitespace parity as the separator above.
                vitalsEl.appendChild(document.createTextNode(' · '));
            }
            if (player.ping != null) {
                const pingEl = document.createElement('span');
                pingEl.className = `mc-latency mc-latency--${player.pingTier}`;
                pingEl.textContent = `${player.ping}ms`;
                vitalsEl.appendChild(pingEl);
            }

            nameCol.appendChild(vitalsEl);
        }

        const detailLine = document.createElement('span');
        detailLine.className = 'small text-muted';
        const parts: string[] = [];
        if (player.dimension) parts.push(resolveMcLabel(player.dimensionLabel, player.dimension));
        if (player.biome) parts.push(resolveMcLabel(player.biomeLabel, player.biome));
        detailLine.textContent = parts.join(' · ');
        nameCol.appendChild(detailLine);

        // Active-effects row -- omitted entirely when the effects array is empty/null (positive-
        // only informational row, same honest-omission posture as the null-ping handling above).
        if (player.effects != null && player.effects.length > 0) {
            const effectsEl = document.createElement('span');
            effectsEl.className = 'mc-player-effects d-flex flex-wrap align-items-center gap-1 mt-1';

            for (const effect of player.effects) {
                if (effect.iconUrl != null) {
                    const iconWrap = document.createElement('span');
                    iconWrap.className = 'mc-effect-icon-wrap';
                    if (effect.title != null) {
                        iconWrap.title = effect.title;
                    }

                    // T-235-03: iconUrl can only ever come from the server's closed
                    // EffectSpriteMap enumeration (App\Utils\EffectSpriteUrl::url() returns null
                    // for any unmapped/modded id) -- an attacker- or mod-controlled effect id can
                    // never reach this assignment. Never construct this URL client-side.
                    const effectImg = document.createElement('img');
                    effectImg.src = effect.iconUrl;
                    effectImg.alt = effect.label;
                    effectImg.width = 24;
                    effectImg.height = 24;
                    effectImg.style.imageRendering = 'pixelated';
                    iconWrap.appendChild(effectImg);

                    if (effect.amplifierRoman != null) {
                        const ampEl = document.createElement('span');
                        ampEl.className = 'mc-effect-amplifier';
                        ampEl.textContent = effect.amplifierRoman;
                        iconWrap.appendChild(ampEl);
                    }

                    effectsEl.appendChild(iconWrap);
                } else {
                    // Unknown/modded effect -- the fallback text chip, never a broken <img>.
                    const fallback = document.createElement('span');
                    fallback.className = 'badge bg-secondary mc-effect-fallback';
                    fallback.style.fontSize = 'var(--ksat-font-size-sm)';
                    fallback.textContent = effect.label;
                    effectsEl.appendChild(fallback);
                }
            }

            nameCol.appendChild(effectsEl);
        }

        row.appendChild(avatarImg);
        row.appendChild(nameCol);
        playersSection.appendChild(row);
    }
}

function updateDetailLastSeen(container: HTMLElement, data: DetailServerData): void {
    const infoPanel = container.querySelector('.mc-detail-info');
    if (!infoPanel) return;

    let lastSeenEl = infoPanel.querySelector<HTMLElement>('.mc-detail-last-seen');

    if (!data.isOnline && data.lastSeenAt) {
        if (!lastSeenEl) {
            lastSeenEl = document.createElement('div');
            lastSeenEl.className = 'text-muted mb-2 mc-detail-last-seen';
            // Insert after the h3 heading
            const heading = infoPanel.querySelector('h3');
            if (heading) {
                heading.after(lastSeenEl);
            }
        }
        lastSeenEl.textContent = '';
        lastSeenEl.append('Last seen: ');
        const timeEl = document.createElement('time');
        // CR-01 (Phase 240 code review) -- identical defect and identical fix to the fleet-card
        // path in updatePlayerSection() above; see the long comment there. `data.lastSeenAt` is
        // `->format('c')` of a Nette-hydrated DATETIME, so its offset is a lie and the raw string
        // must never reach `new Date()`. Both call sites go through the ONE shared helper so this
        // field cannot drift between its two renderers again.
        const utc = wireInstantToUtcIso(data.lastSeenAt);
        if (utc !== null) {
            timeEl.dataset.utc = utc;
        }
        timeEl.dataset.relative = 'always'; // D-01: "Last seen" is relative-forever
        lastSeenEl.appendChild(timeEl);
        localizeInstant(timeEl); // D-03: populate on creation, don't wait for the 60s ticker
    } else if (lastSeenEl) {
        lastSeenEl.remove();
    }
}

// ---------------------------------------------------------------------------
// Clipboard copy
// ---------------------------------------------------------------------------

// Phase 269 (SURF-04): a second .mc-copy-btn (the world seed) now exists inside the same
// .mc-detail container alongside the address button -- the previous singular querySelector()
// would have bound only the FIRST button in DOM order and left the seed button silently inert.
// Bound over every .mc-copy-btn found, each restoring its OWN title text on its own timer.
function initCopyButton(container: HTMLElement): void {
    const btns = container.querySelectorAll<HTMLElement>('.mc-copy-btn');

    btns.forEach((btn) => {
        // Idempotency guard -- a re-init (e.g. this function called again on the same container)
        // must not double-bind a second click listener onto an already-bound button.
        if (btn.dataset.copyBound === '1') return;
        btn.dataset.copyBound = '1';

        btn.addEventListener('click', async (e) => {
            e.preventDefault();
            const address = btn.getAttribute('data-copy');
            if (!address) return;

            try {
                await navigator.clipboard.writeText(address);
                const originalTitle = btn.getAttribute('title') ?? '';
                btn.setAttribute('title', 'Copied!');
                btn.classList.add('mc-copied');

                setTimeout(() => {
                    btn.setAttribute('title', originalTitle);
                    btn.classList.remove('mc-copied');
                }, 1500);
            } catch {
                console.error('[mc] Clipboard write failed');
            }
        });
    });
}

// ---------------------------------------------------------------------------
// Bluemap iframe load detection
// ---------------------------------------------------------------------------

function initBluemap(container: HTMLElement): void {
    const iframe = container.querySelector<HTMLIFrameElement>('.mc-bluemap-iframe');
    const shimmer = container.querySelector<HTMLElement>('.mc-bluemap-shimmer');
    const wrapper = container.querySelector<HTMLElement>('.mc-bluemap-wrapper');
    if (!iframe || !shimmer || !wrapper) return;

    // The URL lives in data-src (not src) so the browser does NOT fetch it on page load.
    // BlueMap is a heavy cross-origin WebGL app served over the homelab tunnel — we lazy-load
    // it only when the map section scrolls near the viewport, not on every detail-page view.
    const src = iframe.dataset.src;
    if (!src) return;

    let settled = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    const clearFallbackTimer = (): void => {
        if (fallbackTimer !== null) { clearTimeout(fallbackTimer); fallbackTimer = null; }
    };

    const reveal = (): void => {
        // Cross-origin: an HTTP error page (e.g. Cloudflare 502 when the server is stopped)
        // also fires 'load' and gets revealed — acceptable. A dead origin fires 'error'.
        if (settled) return;
        settled = true;
        clearFallbackTimer();
        shimmer.style.display = 'none';
        iframe.style.display = 'block';
    };

    const showFallback = (): void => {
        if (settled) return;
        settled = true;
        clearFallbackTimer();
        shimmer.style.display = 'none';
        wrapper.innerHTML = '<div class="text-muted text-center p-4">Map cannot be embedded — use "Open full map" link above</div>';
    };

    iframe.addEventListener('load', reveal);
    iframe.addEventListener('error', showFallback); // network-level failure (refused/DNS/blocked)

    // Drive laziness with an IntersectionObserver on the (always laid-out) wrapper rather
    // than the iframe's loading="lazy" attribute: the iframe starts display:none, and a
    // display:none element never satisfies lazy's intersection check — that combination
    // deadlocked (never loads → 'load' never fires → blind timer shows "cannot be embedded").
    const startLoad = (): void => {
        if (iframe.src) return; // already loading/loaded
        iframe.src = src;
        // Arm the stuck-connection safety net only AFTER load begins, so a not-yet-scrolled
        // map is never mis-flagged. reveal()/showFallback() clear it.
        fallbackTimer = setTimeout(showFallback, 8000);
    };

    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                io.disconnect();
                startLoad();
            }
        }, { rootMargin: '200px' });
        io.observe(wrapper);
    } else {
        startLoad(); // no IntersectionObserver support → load immediately
    }
}
